<template>
    <div class="u-margin-top-30">
        <control :label="'Period ' + (index + 1)">
            <div class="a-form-inputs--half a-form-inputs--half-to">
                <div class="a-form-inputs__container">
                    <date-input v-model="periodStart"
                                name="periodStart"
                                v-field-control:periodStart.required="{type:'date'}"
                                @input="calcRate"
                    ></date-input>
                    <control-error :show="$error('periodStart')">
                        Loss of earnings period must be before the deceased's date of death ({{ theDeceased.dateOfDeath }})
                    </control-error>
                    <control-error :show="!periodEnd || !periodStartIsBeforeEnd()">
                        Start of loss of earnings period must be before end of loss of earnings period
                    </control-error>
                </div>
                <div class="a-form-inputs__to">to</div>
                <div class="a-form-inputs__container">
                    <date-input v-model="periodEnd"
                                name="periodEnd"
                                v-field-control:periodEnd.required="{type:'date'}"
                                @input="calcRate"
                    ></date-input>
                    <control-error :show="$error('periodEnd') || !endPeriodValid()">
                        Loss of earnings period end date must be before {{ theDeceased.dateOfDeath }}
                    </control-error>
                </div>
            </div>
        </control>
        <control label="Earnings Per Annum">
            <select v-model="periodEmploymentType"
                    v-field-control 
                    @input="calcRate">
                <option value="employed">Employed</option>
                <option value="self_employed">Self-employed</option>
            </select>
            <div class="a-form-inputs--half u-margin-top-30">
                <div class="a-form-inputs__container">
                    <select v-model="periodNetGross"
                            v-field-control 
                            @input="calcRate">
                        <option value="gross">Gross</option>
                        <option value="net">Net</option>
                    </select>
                </div>
                <div class="a-form-inputs__container a-form-inputs--pound">
                    <input v-model="periodValue" 
                            autocomplete="off" 
                            name="period_value" 
                            min="0"
                            type="number"
                            v-on:focus="periodValue = periodValue || ''"
                            v-on:blur="periodValue = periodValue || 0"
                            v-two-dp
                            @input="calcRate">
                </div>
            </div>
        </control>

        <control for="sub_total" label="Subtotal"
                 v-show="showSubtotal"
                 before-after="pound">
            <input autocomplete="off" v-model="periodTotal" type="text" disabled>
        </control>

        <button v-if="showDelete" class="a-button" @click="deletePeriod">Delete This Period</button>

        <hr class="hr--margin-bottom">
    </div>
</template>

<script>
    import _ from 'lodash';
    import moment from 'moment';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import {mapGetters, mapActions, mapState} from 'vuex';
    import { setStepValid }  from 'services/routingMethods';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';

    const numberVal = value => parseFloat(value || 0);

    export default {
        name: 'earnings-period',
        props: {
            index: {
                type: Number,
                required: true
            },
            showSubtotal: {
                default: true
            },
            showDelete: {
                default: true
            }
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Modal,
            ModalContent
        },
        mixins: [
            FormValidator
        ],
        computed: {
            ...mapState('fatal/estate/headsOfLoss', ['pastLossOfEarningsPeriods']),
            ...mapGetters('fatal/estate/headsOfLoss', ['findLossOfEarningsPeriod']),
            ...mapGetters('fatal/estate/deceased', ['theDeceased']),
            periodStart: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodStart;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodStart', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            periodEnd: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodEnd;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodEnd', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            includeLastDay: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).includeLastDay;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'includeLastDay', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            includeFirstDay: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).includeFirstDay;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'includeFirstDay', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            periodEmploymentType: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodEmploymentType;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodEmploymentType', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            periodNetGross: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodNetGross;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodNetGross', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            periodValue: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodValue;
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodValue', index: this.index, value}).then(this.calcRate.bind(this));
                }
            },
            periodTotal: {
                get(){
                    return this.findLossOfEarningsPeriod(this.index).periodTotal.toFixed(2);
                },
                set(value){
                    this.updatePastLossOfEarningsPeriodValue({key: 'periodTotal', index: this.index, value});
                }
            },
        },
        methods: {
            ...mapActions('fatal/estate/headsOfLoss', {
                updatePastLossOfEarningsPeriodValue: 'updatePastLossOfEarningsPeriodValue',
                deleteLossOfEarningsPeriod: 'deleteLossOfEarningsPeriod',
                calcLossOfEarningsForPeriod: 'calcLossOfEarningsForPeriod'
            }),
            deletePeriod() {
                this.deleteLossOfEarningsPeriod(this.index);
            },
            calcRate() {
                this.calcLossOfEarningsForPeriod(this.index);
                this.$emit('update');
            },
            endPeriodValid() {
                let mPeriodEnd = moment(this.periodEnd, 'DD/MM/YYYY');
                return mPeriodEnd.isValid() 
                    ? mPeriodEnd.isSameOrBefore(moment(this.theDeceased.dateOfDeath, 'DD/MM/YYYY'))
                    : false;
            },
            periodStartIsBeforeEnd() {
                return this.periodEnd ? moment(this.periodStart, 'DD/MM/YYYY').isBefore(moment(this.periodEnd, 'DD/MM/YYYY')) : false;
            }
        }
    }
</script>


// WEBPACK FOOTER //
// EarningsPeriod.vue?fa00236a